import { Logger } from '@web-publish-id/logger'
import { AuthConfig, AuthLoginCallback } from './types'
import { createDeviceId, getPageLangCode } from '@web-publish-id/shared/utils'
import {
  accountLoginThird,
  accountRefreshToken,
  accountLogout,
  accountRevoke,
  accountRegisterEmail,
  accountLoginEmail,
  accountResetPassword,
  accountForgetPassword,
  accountVerifyForgetPasswordLink,
} from '@web-publish-id/shared/apis'
import { getSDKTokenInfo, setSDKTokenInfo, removeSDKTokenInfo } from '@web-publish-id/shared/caches'
import { GoogleProvider } from './providers/google'
import { isHostOrigin } from './helper/utils'
import { Bridge } from './helper/bridge'
import { FacebookProvider } from './providers/facebook'
import { AuthDeviceInfo } from '@web-publish-id/shared/types'
import { ResultEnum } from '@web-publish-id/shared/enums'
import { injectSecretConfig } from '@web-publish-id/shared/config'
import { signSecret } from './config'
import { DiscordProvider } from './providers/discord'
import { HttpServerError } from '@web-publish-id/shared/http'

/**
 * 认证SDK类 - 提供给第三方网站使用的认证接口
 */
export class AuthCore {
  // 是否是主页面
  private isHost: boolean = isHostOrigin()
  private logger!: Logger
  private googleProvider!: GoogleProvider
  private facebookProvider!: FacebookProvider
  private discordProvider!: DiscordProvider
  private isBridge: boolean = false

  // 是否使用一键登录
  private loginByOneTap: boolean = false

  // 已初始化
  public initialized: boolean = false

  // 语言
  public language: string = getPageLangCode()
  // 设备信息
  public deviceData: AuthDeviceInfo
  // 桥接
  public bridge: Bridge

  // 登录回调
  public loginCallback: AuthLoginCallback

  constructor() {
    this.initDeviceData()
    injectSecretConfig(signSecret)
  }

  /**
   * 初始化
   * @param config
   * @returns
   */
  public async init(config: AuthConfig = {}) {
    this.logger = Logger.getInstance({ prefix: config.logPrefix })
    this.logger.info('auth initializing')

    this.googleProvider = new GoogleProvider()
    await this.googleProvider.installSDK()

    this.facebookProvider = new FacebookProvider()
    this.discordProvider = new DiscordProvider()

    // 如果不是主页面，需要自动设置桥接
    if (!this.isHost) {
      await this.initBridge()
    }

    this.initialized = true

    this.logger.info('auth initialed')
  }

  /**
   * 初始化桥接
   * @param isBridge 是否是桥接页面
   * @returns
   */
  public async initBridge(isBridge?: boolean) {
    this.bridge = new Bridge()
    // 注册方法调用回调
    if (isBridge) {
      this.logger = Logger.getInstance({ prefix: 'Bridge' })
      this.isBridge = isBridge
      this.bridge.onCall('method', (data) => {
        const { method, args } = data
        return Reflect.apply(this[method], this, args)
      })
      this.initialized = true
    } else {
      this.bridge.on('loginSuccess', (res) => this.handleLoginSuccess(res))
      this.bridge.on('loginError', (e) => this.handleLoginError(e))
    }

    return new Promise((resolve, reject) => {
      this.bridge.init({
        isHost: this.isHost,
        onload: () => {
          this.logger.info('auth initialed')
          // 如果是桥接页面，则发送ready事件
          if (isBridge) {
            this.bridge.emit('ready')
          }
          resolve(true)
        },
        onerror: (e) => {
          this.logger.error('auth initialed error', e)
          reject(e)
        },
      })
    })
  }

  /**
   * Google登录
   */
  public ggLogin(callback: AuthLoginCallback) {
    this.loginCallback = callback

    this.googleProvider.oAuth(
      (accessToken) => this.channelAuth(2, accessToken),
      (e) => callback({ err: true })
    )
  }

  /**
   * Google一键登录
   * @param callback
   */
  public ggOneTapLogin(callback: AuthLoginCallback) {
    this.loginCallback = callback

    this.googleProvider.oneTap(
      (idToken) => {
        this.channelAuthById(2, idToken)
        this.loginByOneTap = true
      },
      (e) => callback({ err: true })
    )
  }

  /**
   * Facebook登录
   */
  public fbLogin(callback: AuthLoginCallback) {
    this.loginCallback = callback

    this.facebookProvider.oAuth(
      (accessToken) => this.channelAuth(4, accessToken),
      (e) => callback({ err: true })
    )
  }

  /**
   * Discord登录
   */
  public discordLogin(callback: AuthLoginCallback) {
    this.loginCallback = callback

    this.discordProvider
      .oAuth()
      .then((data) => {
        const { access_token, extend } = data
        this.channelAuth(5, access_token, { extend })
      })
      .catch((e) => callback({ err: true }))
  }

  /**
   * 渠道登录
   * @param channel_type 渠道类型  2.google  3.apple 4-facebook 5-discord
   * @param access_token 授权码
   * @returns
   */
  private async channelAuth(channel_type: number, access_token: string, extra?: any): Promise<any> {
    return this.loginThird({ auth_token: access_token, channel_type, ...extra })
  }

  /**
   * 渠道登录
   * @param channel_type 渠道类型  2.google  3.apple 4-facebook 5-discord
   * @param id_token 授权码
   * @returns
   */
  private async channelAuthById(channel_type: number, id_token: string, extra?: any): Promise<any> {
    return this.loginThird({ id_token, channel_type, ...extra })
  }

  /**
   * 第三方登录
   * @param params
   * @returns
   */
  private async loginThird(params: any): Promise<any> {
    if (!this.isHost) {
      return this.bridge.callMethod('loginThird', ...arguments)
    }

    const sdkTokenInfo = getSDKTokenInfo()

    const data = {
      device_info: this.deviceData,
      language: this.language,
      sdk_token: sdkTokenInfo?.sdk_token,
      ...params,
    }

    const resData = await accountLoginThird(data).catch((e) => {
      this.handleLoginError(e)
      throw e
    })

    if (!resData.sdk_token) {
      this.handleLoginError(new Error('sdk_token is nil'))
      return this.handleLoginError({ err: true })
    }

    setSDKTokenInfo(resData)

    // 登录成功回调
    this.handleLoginSuccess(resData)

    return resData
  }

  /**
   * 邮箱注册
   */
  public async emailRegister(params: { email: string; password: string; code?: string }) {
    this.logger.info('emailRegister', params)

    const data = await accountRegisterEmail({
      ...params,
      language: this.language,
      device_info: this.deviceData,
    }).catch((e) => {
      return { err: true, message: e.message }
    })

    return data
  }

  /**
   * 邮箱登录
   */
  public async emailLogin(
    params: { email: string; credential: string; verify_type: number },
    callback: AuthLoginCallback
  ) {
    this.loginCallback = callback

    const data = await accountLoginEmail({
      ...params,
      language: this.language,
      device_info: this.deviceData,
    }).catch((e) => {
      this.loginResult(e)
      throw e
    })

    setSDKTokenInfo(data)

    // 登录成功回调
    return this.loginResult(data)
  }

  /**
   * 重置密码
   */
  public async resetPassword(params: { password: string; old_password: string }) {
    const { password, old_password } = params

    const sdkTokenInfo = getSDKTokenInfo()

    return await accountResetPassword({
      credential: old_password,
      password,
      verify_type: 1,
      sdk_token: sdkTokenInfo?.sdk_token,
    }).catch((e) => {
      return { err: true, message: e.message }
    })
  }

  /**
   * 忘记密码密码重置
   */
  public async resetPasswordByCode(params: { password: string; credential: string }) {
    return await accountResetPassword({
      ...params,
      verify_type: 2,
    }).catch((e) => {
      return { err: true, message: e.message }
    })
  }

  /**
   * 验证忘记密码
   */
  public async forgetPassword(params: { email: string }) {
    return await accountForgetPassword(params).catch((e) => {
      return { err: true, message: e.message }
    })
  }

  /**
   * 校验重置密码code
   */
  public async verifyResetPasswordCode(params: { credential: string }) {
    return await accountVerifyForgetPasswordLink(params).catch((e) => {
      return { err: true, message: e.message }
    })
  }

  /**
   * 创建登录回调
   * @param callback
   */
  private loginResult(res: any) {
    let data: any = { data: res }

    if (res instanceof Error) {
      if (res instanceof HttpServerError) {
        data = res.toJson()
      } else {
        data = { err: true, message: res.message }
      }
    }

    if (this.loginCallback) {
      this.loginCallback(data)
      this.loginCallback = null
    }

    return data
  }

  /**
   * 处理登录成功回调
   * @param resData
   */
  private async handleLoginSuccess(resData: any) {
    if (this.isBridge) {
      this.bridge.emit('loginSuccess', resData)
    } else {
      this.loginResult(resData)
    }
  }

  /**
   * 处理登录失败回调
   * @param error
   */
  private handleLoginError(resErr?: any) {
    if (this.isBridge) {
      this.bridge.emit('loginError', resErr)
    } else {
      this.logger.error('accountLoginThird res error', resErr)
      this.loginResult(resErr)
    }
  }

  /**
   * 获取SDK Token
   */
  public async getSDKToken() {
    if (!this.isHost) {
      return this.bridge.callMethod('getSDKToken')
    }

    const sdkTokenInfo = getSDKTokenInfo()
    if (!sdkTokenInfo) return null

    const { expiry_at, sdk_token } = sdkTokenInfo
    if (!sdk_token) return null

    if (expiry_at * 1000 < Date.now()) {
      this.logger.info('token expired, refresh token')
      return null
    }

    // 续期
    return this._refreshToken(sdk_token)
      .then((data) => {
        return { data }
      })
      .catch((e) => {
        return this.loginResult(e)
      })
  }

  /**
   * 续期Token
   * @returns
   */
  public async refreshToken() {
    if (!this.isHost) {
      return this.bridge.callMethod('refreshToken')
    }

    const sdkTokenInfo = getSDKTokenInfo()
    if (!sdkTokenInfo) return null

    const { sdk_token } = sdkTokenInfo
    if (!sdk_token) return null

    return this._refreshToken(sdk_token).then((data) => {
      return { data }
    })
  }

  /**
   * 退出登录
   */
  public async logout() {
    // 一件登录需要登出
    if (this.loginByOneTap) {
      this.loginByOneTap = false
      this.googleProvider.signout()
    }

    if (!this.isHost) {
      return this.bridge.callMethod('logout')
    }

    const sdkTokenInfo = getSDKTokenInfo()
    if (!sdkTokenInfo) return

    const { sdk_token } = sdkTokenInfo
    if (!sdk_token) return

    await accountLogout(sdk_token)

    // 退出成功后，清除缓存
    removeSDKTokenInfo()

    this.logger.info('logout success')
  }

  /**
   * 注销账号
   */
  public async revoke() {
    if (!this.isHost) {
      return this.bridge.callMethod('revoke')
    }

    const sdkTokenInfo = getSDKTokenInfo()
    if (!sdkTokenInfo) return

    const { sdk_token } = sdkTokenInfo
    if (!sdk_token) return

    await accountRevoke(sdk_token)

    // 注销成功后，清除缓存
    removeSDKTokenInfo()

    this.logger.info('revoke success')
  }

  /**
   * 初始化设备数据
   */
  private async initDeviceData() {
    const ori_device_id = await createDeviceId()

    const data: AuthDeviceInfo = {
      ori_device_id,
      os: 1,
    }
    this.deviceData = data
  }

  /**
   * Token续期
   */
  private async _refreshToken(sdkToken: string) {
    const data = await accountRefreshToken({ sdk_token: sdkToken })

    // 续期成功后，更新缓存
    setSDKTokenInfo(data)

    this.logger.debug('refreshToken success', data)

    return data
  }
}

export default AuthCore
